/* eslint-disable react/no-unescaped-entities */
"use client";
import React, { useState } from "react";
import Link from "next/link";

const PrivacyPage = () => {
  const [searchTerm, setSearchTerm] = useState("");

  // Define all the headings you want to filter
  const headings = [
    "Privacy Policy",
    "Our Privacy Policy Explains:",
    "Account Information",
    "Physiological and Usage Data",
    "Anonymized Data",
    "Customer Support Inquiries",
    "Monitoring",
    "Device Data",
    "How We Use the Information We Collect",
    "How We Share the Information We Collect",
    "Controller",
    "Processor",
    "To Contact You",
    "How to Protect Your Personal Information",
    "Updates and Revisions to Our Privacy Policy",
    "Information Submission by Minors",
  ];

  // Filter headings based on search term
  const filteredHeadings = headings.filter((heading) =>
    heading.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col justify-between gap-8 lg:flex-row">
        <div className="lg:w-3/4">
          <div className="relative w-full">
            <SearchIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400" />
            <input
              className="w-full rounded-md border py-2 pl-10 pr-4"
              placeholder="Search our help articles"
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <br />
          <article>
            <h1 className="text-5xl font-bold">Privacy Policy</h1>
            <p className="mt-2 text-sm text-gray-500">Updated 3 months ago</p>
            <p className="mt-2 text-sm text-gray-500">
              Updated February 22, 2024
            </p>
            {filteredHeadings.includes("Privacy Policy") && (
              <>
                <p className="my-6">
                  This Privacy Policy describes how Saintiant Technologies Pvt
                  Ltd (collectively “Saintiant Technologies,” “we,” or “us”)
                  collects, processes, uses, discloses, and secures information
                  through its mobile app, Spanda, its digital stethoscope
                  device, and any associated software applications,
                  functionalities delivered through third parties (collectively
                  the “Services”). It relates to the immediate user of the
                  Services (“You” or “User”) and individuals such as
                  doctors/veterinary doctors, para-medicals, caretakers, and
                  other users (collectively known as “Users”).
                </p>
              </>
            )}
            {filteredHeadings.includes("Our Privacy Policy Explains:") && (
              <>
                <h2 className="mb-4 mt-6 text-3xl font-bold">
                  Our Privacy Policy Explains:
                </h2>
                <ul className="list-disc space-y-2 pl-5">
                  <li>
                    The information we collect, why we need it, and how we use
                    it.
                  </li>
                  <li>
                    What choices you can make about how we use your information.
                  </li>
                  <li>
                    The measures we take to protect the security of the
                    information.
                  </li>
                </ul>
              </>
            )}
            {filteredHeadings.includes("Account Information") && (
              <>
                <h2 className="mb-4 mt-6 text-3xl font-bold">
                  Account Information
                </h2>
                <p className="my-6">
                  When you register to use Spanda or create an account, we may
                  collect your name and other information provided to us, such
                  as your contact details, password. We also collect any
                  information uploaded or otherwise input by you while using
                  Spanda. You may add information to your profile such as
                  Patient name, and information about their medical conditions,
                  and medications. We use this information to create your
                  account and provide you with Spanda's services.
                </p>
              </>
            )}
            {filteredHeadings.includes("Physiological and Usage Data") && (
              <>
                <h2 className="mb-4 mt-6 text-3xl font-bold">
                  Physiological and Usage Data
                </h2>
                <p className="my-6">
                  We collect certain information through your use of Spanda
                  and/or your smartphone connected to the app, such as, but not
                  limited to: auscultation’s data, diagnosed condition and
                  relevant notes, average heart rate, and geographic location of
                  data acquisition. We may collect such information from
                  patients or from healthcare providers. We use this information
                  to provide and improve Spanda's services and provide
                  application functionalities.
                </p>
              </>
            )}
            {filteredHeadings.includes("Anonymized Data") && (
              <>
                <h2 className="mb-4 mt-6 text-3xl font-bold">
                  Anonymized Data
                </h2>
                <p className="my-6">
                  In an ongoing effort to improve our product and better serve
                  our Customers, Saintiant Technologies conducts research based
                  on data, including the Personal Information we collect from
                  you and the other information provided to us. We compile and
                  analyze any data we collect both on an aggregate basis and on
                  a de-identified and pseudonymized basis to produce research
                  and analyses. De-identified data and pseudonymized data cannot
                  be used to identify you. Anonymized data cannot be used to
                  identify you and is unable to be re-identified and is no
                  longer considered Personal Information. While the data
                  Saintiant Technologies uses to create the research and
                  analysis may be de-identified or pseudonymized, the research
                  and analyzed products will be anonymized. Saintiant
                  Technologies may share the research and analysis in aggregated
                  and anonymized format with its affiliates, and healthcare
                  research and services entities. The research and analysis
                  cannot be used to identify you personally and cannot be
                  re-identified. Saintiant Technologies may disclose aggregated,
                  de-identified and/or pseudonymised information in order to
                  describe our business and the Services to current and
                  prospective business partners and Customers, and to other
                  third parties for other lawful purposes. Saintiant
                  Technologies retains anonymized files of the audio recordings
                  along with relevant attributes from Devices. These do not
                  contain any associated Personal Information.
                </p>
              </>
            )}
            {filteredHeadings.includes("Customer Support Inquiries") && (
              <>
                <h2 className="mb-4 mt-6 text-3xl font-bold">
                  Customer Support Inquiries
                </h2>
                <p className="my-6">
                  You can contact us via our customer support or
                  contact@spanda.life if you have any questions. In the process,
                  we may also collect technical information about your device,
                  including your IP address or device ID, which we will use to
                  provide you with support and to improve our services. Contact
                  us at contact@spanda.life if you have any questions.
                </p>
              </>
            )}
            {filteredHeadings.includes("Monitoring") && (
              <>
                <h2 className="mb-4 mt-6 text-3xl font-bold">Monitoring</h2>
                <p className="my-6">
                  Saintiant Technologies and its affiliates and agents may, but
                  are not obligated to, review and/or retain information and/or
                  communications stored and/or transmitted using the Services
                  (“User Content”). We may monitor User Content for data
                  collection purposes and/or to evaluate the quality of service
                  you receive, your compliance with the Terms of Use, the
                  security of the Services, or for other reasons.
                </p>
                <p className="my-6">
                  If you are a patient, your healthcare provider(s) may also
                  monitor User Content to monitor your progress and overall
                  condition and to follow up with you, as they deem appropriate
                  in their independent judgment as your healthcare providers.
                </p>
                <p className="my-6">
                  You agree that such monitoring activities, if in compliance
                  with applicable privacy laws, will not entitle you to any
                  cause of action or other right regarding the manner in which
                  Saintiant Technologies or its affiliates or agents monitor
                  your communications and enforce or fail to enforce the Terms
                  of this agreement. In no event will Saintiant Technologies or
                  any of its affiliates or agents be liable for any costs,
                  damages, expenses, or any other liabilities incurred by you as
                  a result of monitoring activities by Saintiant Technologies or
                  its affiliates or agents.
                </p>
              </>
            )}
            {filteredHeadings.includes("Device Data") && (
              <>
                <h2 className="mb-4 mt-6 text-3xl font-bold">Device Data</h2>
                <p className="my-6">
                  Information collected from your Device(s) includes: the model
                  number, serial number, hardware and software versions, and
                  other technical information, like your usage statistics and
                  settings. This information is used to improve the performance
                  of Spanda and our products and services, and to provide you
                  with better support.
                </p>
              </>
            )}
            {filteredHeadings.includes(
              "How We Use the Information We Collect",
            ) && (
              <>
                <h2 className="mb-4 mt-6 text-3xl font-bold">
                  How We Use the Information We Collect
                </h2>
                <p className="my-6">
                  We use the information we collect to provide, maintain, and
                  improve our services, to develop new services, and to protect
                  Saintiant Technologies and our users. We also use the
                  information we collect to:
                </p>
                <ul className="list-disc space-y-2 pl-5">
                  <li>Send you updates and alerts related to Spanda</li>
                  <li>Provide customer support</li>
                  <li>Monitor the usage and performance of our services</li>
                  <li>
                    Conduct research and development to improve our services
                  </li>
                </ul>
              </>
            )}
            {filteredHeadings.includes(
              "How We Share the Information We Collect",
            ) && (
              <>
                <h2 className="mb-4 mt-6 text-3xl font-bold">
                  How We Share the Information We Collect
                </h2>
                <p className="my-6">
                  We do not share your personal information with companies,
                  organizations, or individuals outside of Saintiant
                  Technologies, except in the following cases:
                </p>
                <ul className="list-disc space-y-2 pl-5">
                  <li>With your consent</li>
                  <li>For external processing</li>
                  <li>For legal reasons</li>
                </ul>
                <p className="my-6">
                  We may share aggregated, non-personally identifiable
                  information publicly and with our partners. For example, we
                  may share information publicly to show trends about the
                  general use of our services.
                </p>
              </>
            )}
            {filteredHeadings.includes("Controller") && (
              <>
                <h2 className="mb-4 mt-6 text-3xl font-bold">Controller</h2>
                <p className="my-6">
                  Saintiant Technologies Pvt Ltd is the data controller for the
                  information we collect through Spanda. This means that we are
                  responsible for determining the purposes and means of
                  processing your personal information.
                </p>
              </>
            )}
            {filteredHeadings.includes("Processor") && (
              <>
                <h2 className="mb-4 mt-6 text-3xl font-bold">Processor</h2>
                <p className="my-6">
                  In some cases, Saintiant Technologies acts as a data processor
                  on behalf of another entity, such as a healthcare provider.
                  When we act as a data processor, we process your personal
                  information only as instructed by the data controller.
                </p>
              </>
            )}
            {filteredHeadings.includes("To Contact You") && (
              <>
                <h2 className="mb-4 mt-6 text-3xl font-bold">To Contact You</h2>
                <p className="my-6">
                  We may use the contact information you provide to us to
                  contact you with updates and alerts related to Spanda, as well
                  as with marketing and promotional materials that we think may
                  be of interest to you.
                </p>
              </>
            )}
            {filteredHeadings.includes(
              "How to Protect Your Personal Information",
            ) && (
              <>
                <h2 className="mb-4 mt-6 text-3xl font-bold">
                  How to Protect Your Personal Information
                </h2>
                <p className="my-6">
                  We take the security of your personal information very
                  seriously and have implemented measures to protect it from
                  unauthorized access, use, or disclosure. These measures
                  include:
                </p>
                <ul className="list-disc space-y-2 pl-5">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Regular security audits and assessments</li>
                  <li>Strict access controls and authentication mechanisms</li>
                </ul>
              </>
            )}
            {filteredHeadings.includes(
              "Updates and Revisions to Our Privacy Policy",
            ) && (
              <>
                <h2 className="mb-4 mt-6 text-3xl font-bold">
                  Updates and Revisions to Our Privacy Policy
                </h2>
                <p className="my-6">
                  We may update this Privacy Policy from time to time. We will
                  notify you of any changes by posting the new Privacy Policy on
                  Spanda and updating the “Last Updated” date at the top of this
                  page. You are advised to review this Privacy Policy
                  periodically for any changes.
                </p>
              </>
            )}
            {filteredHeadings.includes("Information Submission by Minors") && (
              <>
                <h2 className="mb-4 mt-6 text-3xl font-bold">
                  Information Submission by Minors
                </h2>
                <p className="my-6">
                  Our services are not intended for use by individuals under the
                  age of 18. We do not knowingly collect personal information
                  from children under 18. If we become aware that we have
                  collected personal information from a child under 18, we will
                  take steps to delete such information.
                </p>
              </>
            )}
          </article>
        </div>
        <aside className="space-y-6 lg:w-1/4">
          <div>
            <h2 className="mb-2 text-xl font-bold">Compliance and Policies</h2>
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <Link href="/faq" className="text-blue-600 hover:text-blue-700">
                  FAQ
                </Link>
              </li>
              <li>
                <Link
                  href="/contact"
                  className="text-blue-600 hover:text-blue-700"
                >
                  Contact
                </Link>
              </li>
              <li>
                <Link
                  href="/download"
                  className="text-blue-600 hover:text-blue-700"
                >
                  Manual
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h2 className="mb-2 text-xl font-bold">Need Help?</h2>
            <p>
              If you have any questions or need further assistance, please
              contact our support team.
            </p>
            <Link
              href="mailto:support@ekohealth.com"
              className="text-blue-600 hover:text-blue-700"
            >
              contact@saintiant.tech
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
};

function SearchIcon(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

export default PrivacyPage;
